/**
 * ESPN → internal model.
 *
 * The vendor's field names stop here. Everything downstream sees the same
 * shapes the mock provider produces, so no component knows or cares where
 * a game came from (Implementation Guide §20, §25).
 *
 * Two rules govern this file:
 *
 *  1. **Never invent state.** Where ESPN gives authoritative down,
 *     distance, score, clock and possession, we use them verbatim rather
 *     than re-deriving with the game engine. The engine exists to simulate
 *     the mock game, not to second-guess a broadcast (PRD §34).
 *
 *  2. **Unknown means unknown.** A play type we don't recognise maps to
 *     `null`, which shows the play with its description and no explanation.
 *     A mislabelled explanation is worse than a missing one.
 *
 * Every mapping below was written against real captured responses in
 * `__fixtures__/`, not against documentation: this API has none.
 */

import { EVENT_TYPES } from "../../data/concepts/eventConceptMap";
import { GAME_STATUS } from "../../utils/gameEngine";
import { TEAMS_BY_ID } from "../../data/teams";

/** ESPN uses WSH for Washington; we use WAS. Everything else matches. */
const TEAM_ALIASES = Object.freeze({ WSH: "WAS" });

export function normalizeTeamId(abbreviation) {
  const abbr = String(abbreviation ?? "").trim().toUpperCase();
  const mapped = TEAM_ALIASES[abbr] ?? abbr;
  return TEAMS_BY_ID[mapped] ? mapped : null;
}

/**
 * PLAY TEXT SPEAKS A DIFFERENT DIALECT
 * ------------------------------------
 * `TEAM_ALIASES` above covers the abbreviation ESPN puts in structured
 * fields. The prose of a play description does not use those. It carries
 * the league gamebook's own abbreviations, and five of the thirty-two
 * clubs are spelled differently there:
 *
 *   ARZ  Arizona    BLT  Baltimore    CLV  Cleveland
 *   HST  Houston    LA   Los Angeles Rams
 *
 * Washington is the mirror image and needs no row: play text writes WAS,
 * which is already our id, while the structured fields write WSH.
 *
 * That matters because the club is the one piece of disproof the text
 * offers about who a name belongs to: "PENALTY on KC-J.Taylor" says
 * Kansas City, out loud, in the sentence. Reading "BLT" as an unknown
 * club would silently switch that check off for Baltimore.
 *
 * The table is not a guess. All sixteen games of 2025 regular season
 * week 1 were read, the prefixes appearing in their play text extracted,
 * and compared with the two clubs in each game. Sixteen games cover all
 * thirty-two clubs exactly once, so every abbreviation below is one ESPN
 * actually wrote, and the list is complete.
 */
const PLAY_TEXT_TEAM_ALIASES = Object.freeze({
  ARZ: "ARI",
  BLT: "BAL",
  CLV: "CLE",
  HST: "HOU",
  LA: "LAR",
});

/**
 * A club abbreviation as it appears inside play-by-play prose.
 *
 * Null for anything that is not a club, which is the answer that matters
 * most: play text writes "Center-T.Sieg" and "Holder-B.Anger" in the same
 * shape as a club prefix, and neither of those is a team.
 */
export function normalizePlayTextTeamId(abbreviation) {
  const abbr = String(abbreviation ?? "").trim().toUpperCase();
  return normalizeTeamId(PLAY_TEXT_TEAM_ALIASES[abbr] ?? abbr);
}

/** Our id back to ESPN's, derived from the same alias map so the two
 * directions can never drift apart. */
const ESPN_ABBREVIATIONS = Object.freeze(
  Object.entries(TEAM_ALIASES).reduce(
    (acc, [espn, ours]) => ({ ...acc, [ours]: espn }),
    {}
  )
);

/**
 * The abbreviation to put in an outbound ESPN URL.
 *
 * ESPN's team-scoped endpoints accept the abbreviation in place of their
 * numeric team id, which spares us a hand-written id table. The one
 * abbreviation that differs is Washington, and asking for `WAS` is a hard
 * 400 rather than a redirect, so the alias has to be applied on the way
 * out as well as on the way in. Verified against all 32 clubs.
 *
 * Returns null for anything that is not one of our teams, so a caller
 * cannot accidentally build a URL out of user input.
 */
export function toEspnTeamAbbreviation(teamId) {
  const abbr = String(teamId ?? "").trim().toUpperCase();
  if (!TEAMS_BY_ID[abbr]) return null;
  return ESPN_ABBREVIATIONS[abbr] ?? abbr;
}

/**
 * ESPN's status names.
 *
 * WHY THE LAST FOUR ROWS EXIST
 * ----------------------------
 * This map used to hold the first six, and everything else fell through
 * to `SCHEDULED` while the competitor scores were copied across
 * untouched. A called-off game therefore arrived downstream as an
 * ordinary upcoming fixture carrying a scoreline, was accepted by the
 * cross-device cache (which only refuses a payload with an IN_PROGRESS
 * game in it), and could sit there for the six-hour idle TTL being
 * painted as tonight's game. Absent statuses are not a cosmetic gap.
 *
 * WHAT WAS ACTUALLY VERIFIED, AND WHAT WAS NOT
 * --------------------------------------------
 * Captured from ESPN on 12 August 2026, not taken from documentation,
 * because this API has none:
 *
 *   STATUS_SCHEDULED   id 1,  state "pre",  completed false
 *   STATUS_FINAL       id 3,  state "post", completed true
 *   STATUS_CANCELED    id 5,  state "post", completed false
 *                      NFL event 401437947 (BUF at CIN, 2 January 2023),
 *                      still served today, with period 1 and a 6:12 clock
 *                      on it: the abandoned-in-progress case exactly.
 *   STATUS_POSTPONED   id 6,  state "post", completed false
 *                      104 MLB events across 2023-2025 and 3 college
 *                      football events across 2021-2025.
 *
 * Note the American spelling of CANCELED. It is ESPN's key, not our
 * prose, so it stays as they publish it.
 *
 * STATUS_SUSPENDED and STATUS_DELAYED are NOT verified. Both are live,
 * transient states: a sweep of six NFL seasons, six MLB seasons, five
 * college football seasons and sixty soccer leagues found only the four
 * above persisted, because a suspended or delayed game is a final or a
 * postponement by the time anyone reads it back. They are mapped here on
 * the strength of the naming convention alone, which is a guess about a
 * spelling and is written down as one. Correctness does not depend on
 * them: an unrecognised name is handled by normalizeStatusType below.
 */
const STATUS_MAP = Object.freeze({
  STATUS_SCHEDULED: GAME_STATUS.SCHEDULED,
  STATUS_IN_PROGRESS: GAME_STATUS.IN_PROGRESS,
  STATUS_HALFTIME: GAME_STATUS.IN_PROGRESS,
  STATUS_END_PERIOD: GAME_STATUS.IN_PROGRESS,
  STATUS_FINAL: GAME_STATUS.FINAL,
  STATUS_FINAL_OVERTIME: GAME_STATUS.FINAL,

  STATUS_CANCELED: GAME_STATUS.CANCELLED,
  STATUS_POSTPONED: GAME_STATUS.POSTPONED,
  STATUS_SUSPENDED: GAME_STATUS.SUSPENDED,
  STATUS_DELAYED: GAME_STATUS.DELAYED,
});

/** The status name alone. Null for anything not in the map: never a guess. */
export function normalizeStatus(name) {
  return STATUS_MAP[String(name ?? "").toUpperCase()] ?? null;
}

/**
 * A whole `status.type` object, which is what a game record needs.
 *
 * The name is tried first because it is the specific answer. When we do
 * not recognise it there is exactly one further inference worth making,
 * and it is ESPN's own assertion rather than ours: `state: "post"` with
 * `completed: true` means the game was played to its end. That pairing
 * held on every completed event in the sweep above, including spellings
 * this app has never seen (STATUS_FULL_TIME, STATUS_FINAL_AET,
 * STATUS_FINAL_PEN), so a future NFL spelling of "finished" still reads
 * as finished instead of as an unknown.
 *
 * Everything else is UNKNOWN, deliberately. `state: "pre"` is the
 * tempting one to call SCHEDULED, and it is precisely the assumption
 * that caused this bug, so it is not made: a row we cannot name is never
 * promoted into a fixture that is about to kick off.
 */
export function normalizeStatusType(type) {
  const byName = normalizeStatus(type?.name);
  if (byName) return byName;

  const state = String(type?.state ?? "").toLowerCase();
  if (state === "post" && type?.completed === true) return GAME_STATUS.FINAL;

  return GAME_STATUS.UNKNOWN;
}

const SEASON_TYPE_MAP = Object.freeze({
  1: "preseason",
  2: "regular",
  3: "playoffs",
  4: "superbowl",
});

/**
 * Play type text → our event taxonomy.
 * Keys are the exact `type.text` values ESPN returns.
 */
const PLAY_TYPE_MAP = Object.freeze({
  Rush: EVENT_TYPES.RUN,
  "Pass Reception": EVENT_TYPES.PASS_COMPLETE,
  "Pass Incompletion": EVENT_TYPES.PASS_INCOMPLETE,
  Sack: EVENT_TYPES.SACK,
  Kickoff: EVENT_TYPES.KICKOFF,
  "Kickoff Return (Offense)": EVENT_TYPES.KICKOFF,
  Punt: EVENT_TYPES.PUNT,
  Penalty: EVENT_TYPES.PENALTY,
  "Field Goal Good": EVENT_TYPES.FIELD_GOAL,
  "Field Goal Missed": EVENT_TYPES.FIELD_GOAL_MISSED,
  "Blocked Field Goal": EVENT_TYPES.FIELD_GOAL_MISSED,
  "Passing Touchdown": EVENT_TYPES.TOUCHDOWN,
  "Rushing Touchdown": EVENT_TYPES.TOUCHDOWN,
  "Interception Return Touchdown": EVENT_TYPES.INTERCEPTION,
  "Fumble Return Touchdown": EVENT_TYPES.FUMBLE,
  "Interception Return": EVENT_TYPES.INTERCEPTION,
  Interception: EVENT_TYPES.INTERCEPTION,
  "Fumble Recovery (Own)": EVENT_TYPES.FUMBLE,
  "Fumble Recovery (Opponent)": EVENT_TYPES.FUMBLE,
  "Extra Point Good": EVENT_TYPES.EXTRA_POINT,
  "Extra Point Missed": EVENT_TYPES.EXTRA_POINT,
  "Two-point Rush": EVENT_TYPES.TWO_POINT_CONVERSION,
  "Two-point Pass": EVENT_TYPES.TWO_POINT_CONVERSION,
  "End Period": EVENT_TYPES.END_OF_QUARTER,
  "End of Half": EVENT_TYPES.END_OF_QUARTER,
  "End of Game": EVENT_TYPES.END_OF_GAME,
  "End of Regulation": EVENT_TYPES.END_OF_QUARTER,
});

/**
 * Administrative rows in ESPN's play list that aren't plays. Dropping them
 * keeps the drive tracker readable: a beginner does not need to see
 * "Official Timeout" three times a drive.
 */
const IGNORED_PLAY_TYPES = new Set([
  "Timeout",
  "Official Timeout",
  "Two-minute warning",
  "Coin Toss",
]);

export function normalizePlayType(text) {
  return PLAY_TYPE_MAP[String(text ?? "").trim()] ?? null;
}

export function isIgnoredPlay(text) {
  return IGNORED_PLAY_TYPES.has(String(text ?? "").trim());
}

/** "12:34" → seconds remaining. */
export function normalizeClock(displayValue) {
  const match = String(displayValue ?? "").match(/^(\d{1,2}):(\d{2})$/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

const competitorsOf = (competition) => {
  const list = competition?.competitors ?? [];
  return {
    home: list.find((c) => c.homeAway === "home") ?? null,
    away: list.find((c) => c.homeAway === "away") ?? null,
  };
};

/* ------------------------------------------------------------------ game */

/**
 * One scheduled/live/finished game.
 * Returns null when either team can't be resolved: a half-mapped game is
 * worse than a missing one.
 */
export function normalizeGame(event) {
  const competition = event?.competitions?.[0];
  if (!competition) return null;

  const { home, away } = competitorsOf(competition);
  const homeTeamId = normalizeTeamId(home?.team?.abbreviation);
  const awayTeamId = normalizeTeamId(away?.team?.abbreviation);
  if (!homeTeamId || !awayTeamId) return null;

  const kickoffAt = Date.parse(event.date ?? competition.date ?? "");

  return {
    gameId: `nfl-${event.id}`,
    providerGameId: String(event.id),
    provider: "nfl",
    isSimulated: false,

    season: event.season?.year ?? null,
    seasonType: SEASON_TYPE_MAP[event.season?.type] ?? "regular",
    seasonTypeId: event.season?.type ?? null,
    week: event.week?.number ?? null,

    homeTeamId,
    awayTeamId,
    kickoffAt: Number.isFinite(kickoffAt) ? kickoffAt : null,
    venue: competition.venue?.fullName ?? null,

    status: normalizeStatusType(competition.status?.type),
    statusDetail: competition.status?.type?.shortDetail ?? null,

    homeScore: Number(home?.score ?? 0),
    awayScore: Number(away?.score ?? 0),

    hasSimulation: false,
    totalPlays: 0,
  };
}

export function normalizeSchedule(payload) {
  return (payload?.events ?? []).map(normalizeGame).filter(Boolean);
}

/**
 * `cdn.espn.com/core/nfl/schedule?xhr=1` returns games grouped by date
 * (`content.schedule["20260813"].games`) rather than as a flat list. The
 * games themselves are the same shape the scoreboard uses.
 */
export function normalizeScheduleDays(payload) {
  const byDay = payload?.content?.schedule ?? {};

  return Object.keys(byDay)
    .sort()
    .flatMap((day) => byDay[day]?.games ?? [])
    .map(normalizeGame)
    .filter(Boolean);
}

/* ----------------------------------------------------------------- state */

/**
 * Live game state, taken from ESPN rather than derived.
 *
 * `situation` only exists while a game is actually in progress, so down,
 * distance and possession are null before kickoff and after full time,
 * which is correct, and the UI already handles it.
 */
export function normalizeState(event, { at = Date.now() } = {}) {
  const game = normalizeGame(event);
  if (!game) return null;

  const competition = event.competitions[0];
  const { home, away } = competitorsOf(competition);
  const situation = competition.situation ?? null;

  // `situation.possession` is an ESPN team id, not an abbreviation.
  const possessionEspnId = situation?.possession ?? null;
  const possession =
    possessionEspnId == null
      ? null
      : String(home?.team?.id) === String(possessionEspnId)
        ? game.homeTeamId
        : String(away?.team?.id) === String(possessionEspnId)
          ? game.awayTeamId
          : null;

  // Deliberately null. The scoreboard's `situation.yardLine` is an absolute
  // field position whose convention we have not been able to verify against
  // a live game, and guessing it would put the ball in the wrong place on
  // the field diagram. The play feed carries `yardsToEndzone`, which IS
  // verified, so `normalizeEvent` supplies ball position instead and the
  // field view falls back to its placeholder until a play arrives.
  const ballPosition = null;

  return {
    gameId: game.gameId,
    status: game.status,

    quarter: competition.status?.period ?? 1,
    clock: normalizeClock(competition.status?.displayClock) ?? 0,

    homeTeamId: game.homeTeamId,
    awayTeamId: game.awayTeamId,
    homeScore: game.homeScore,
    awayScore: game.awayScore,

    possession,
    down: situation?.down ?? null,
    distance: situation?.distance ?? null,
    ballPosition,
    downDistanceText: situation?.downDistanceText ?? null,
    inRedZone: Boolean(situation?.isRedZone),

    latestEvent: null,
    isSimulated: false,
    provider: "nfl",
    lastUpdatedAt: at,
  };
}

/* ---------------------------------------------------------------- events */

/**
 * One play.
 *
 * `ballPosition` is 0–100 from the possessing team's own goal line, which
 * ESPN gives directly as `yardsToEndzone`, verified against real drives
 * in `__fixtures__/espn-summary.json`.
 */
export function normalizeEvent(play, context = {}) {
  const typeText = play?.type?.text;
  if (!play || isIgnoredPlay(typeText)) return null;

  const type = normalizePlayType(typeText);
  const teamId =
    context.teamIdOf?.(play.start?.team?.id) ?? context.driveTeamId ?? null;

  const start = play.start ?? {};
  const end = play.end ?? {};

  const homeScore = Number(play.homeScore ?? 0);
  const awayScore = Number(play.awayScore ?? 0);
  const pointsScored =
    homeScore - (context.previousHomeScore ?? homeScore) +
    (awayScore - (context.previousAwayScore ?? awayScore));

  const startDistance = Number(start.distance);
  const endDown = Number(end.down);

  return {
    id: `nfl-play-${play.id ?? play.sequenceNumber}`,
    index: context.index ?? 0,
    driveNumber: context.driveNumber ?? 0,

    // Null type means "we have no explanation for this", not "ignore it".
    type: type ?? typeText ?? "UNKNOWN",
    knownType: Boolean(type),

    teamId,
    yards: Number.isFinite(Number(play.statYardage)) ? Number(play.statYardage) : null,
    description: play.text ?? "",

    quarter: play.period?.number ?? null,
    clock: normalizeClock(play.clock?.displayValue) ?? null,

    resultingDown: Number.isFinite(endDown) ? endDown : null,
    resultingDistance: Number.isFinite(Number(end.distance)) ? Number(end.distance) : null,
    resultingBallPosition: Number.isFinite(Number(end.yardsToEndzone))
      ? 100 - Number(end.yardsToEndzone)
      : null,

    startDown: Number.isFinite(Number(start.down)) ? Number(start.down) : null,
    startDistance: Number.isFinite(startDistance) ? startDistance : null,
    startBallPosition: Number.isFinite(Number(start.yardsToEndzone))
      ? 100 - Number(start.yardsToEndzone)
      : null,

    homeScore,
    awayScore,
    pointsScored: Math.max(0, pointsScored),

    // ESPN flags these directly, so we don't have to infer them.
    firstDown: Boolean(
      Number.isFinite(endDown) && endDown === 1 && Number(start.down) > 0 && !play.isTurnover
    ),
    turnover: Boolean(play.isTurnover),
    isPenalty: Boolean(play.isPenalty),
    scoringPlay: Boolean(play.scoringPlay),
  };
}

/**
 * Flatten ESPN's drive list into our ordered event log.
 * Drives arrive oldest-first; so does the result.
 */
export function normalizeDrives(summary) {
  const drives = summary?.drives?.previous ?? [];

  const competitors =
    summary?.header?.competitions?.[0]?.competitors ?? [];
  const byEspnId = new Map(
    competitors.map((c) => [String(c.team?.id), normalizeTeamId(c.team?.abbreviation)])
  );
  const teamIdOf = (espnId) => byEspnId.get(String(espnId)) ?? null;

  const events = [];
  let index = 0;
  let previousHomeScore = 0;
  let previousAwayScore = 0;

  drives.forEach((drive, driveIndex) => {
    const driveTeamId = normalizeTeamId(drive.team?.abbreviation);

    (drive.plays ?? []).forEach((play) => {
      const event = normalizeEvent(play, {
        index,
        driveNumber: driveIndex + 1,
        driveTeamId,
        teamIdOf,
        previousHomeScore,
        previousAwayScore,
      });
      if (!event) return;

      previousHomeScore = event.homeScore;
      previousAwayScore = event.awayScore;
      index += 1;
      events.push(event);
    });
  });

  return events;
}

export const __testing = {
  PLAY_TYPE_MAP,
  IGNORED_PLAY_TYPES,
  TEAM_ALIASES,
  PLAY_TEXT_TEAM_ALIASES,
};
